import React, { useState, useEffect } from 'react';
// import { base44 } from '@/api/base44Client';
import Icon from '../components/ui/Icon';
import ListingCard from '../components/listings/ListingCard';
import ListingForm from '../components/listings/ListingForm';

const placeholderListings = [
  {
    id: '1',
    title: 'Cozy Single Family Home',
    address: '123 Main St',
    city: 'Miami',
    list_price: 450000,
    status: 'active',
    property_type: 'single_family'
  },
  {
    id: '2',
    title: 'Downtown Condo',
    address: '456 Center Ave',
    city: 'Orlando',
    list_price: 350000,
    status: 'pending',
    property_type: 'condo'
  },
  {
    id: '3',
    title: 'Suburban Townhouse',
    address: '789 Oak Dr',
    city: 'Tampa',
    list_price: 300000,
    status: 'sold',
    property_type: 'townhouse'
  }
];

const placeholderAgents = [
  { id: 'a1', full_name: 'Alice Johnson', status: 'active' },
  { id: 'a2', full_name: 'Bob Smith', status: 'active' }
];

export default function Listings() {
  const [listings, setListings] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingListing, setEditingListing] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [viewMode, setViewMode] = useState('grid');

 const loadData = async () => {
  // use local placeholder data instead of base44 API
  const listingsData = placeholderListings; // your local dummy listings array
  const agentsData = placeholderAgents;     // your local dummy agents array

  setListings(listingsData);
  setAgents(agentsData);
  setLoading(false);
};


  useEffect(() => { loadData(); }, []);

  const handleSave = async (data) => {
  if (editingListing) {
    // Update existing listing in state
    setListings(prev =>
      prev.map(l => (l.id === editingListing.id ? { ...l, ...data } : l))
    );
  } else {
    // Add new listing with a temporary id
    setListings(prev => [...prev, { id: Date.now().toString(), ...data }]);
  }

  setShowForm(false);
  setEditingListing(null);

  // Refresh listings (still works with local data)
  loadData();
};

  const handleEdit = (listing) => {
    setEditingListing(listing);
    setShowForm(true);
  };

  const filteredListings = listings.filter(listing => {
    const matchesSearch = !search ||
      listing.title?.toLowerCase().includes(search.toLowerCase()) ||
      listing.address?.toLowerCase().includes(search.toLowerCase()) ||
      listing.city?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || listing.status === statusFilter;
    const matchesType = typeFilter === 'all' || listing.property_type === typeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });

  const statusCounts = {
    all: listings.length,
    active: listings.filter(l => l.status === 'active').length,
    pending: listings.filter(l => l.status === 'pending').length,
    sold: listings.filter(l => l.status === 'sold').length
  };

  const totalValue = listings.filter(l => l.status === 'active').reduce((sum, l) => sum + (l.list_price || 0), 0);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--slate-50)', padding: '2rem' }}>
      <div style={{ maxWidth: '80rem', margin: '0 auto' }}>
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Listings</h1>
            <p className="text-slate-500 mt-1">
              {statusCounts.active} active · ${(totalValue / 1000000).toFixed(1)}M total value
            </p>
          </div>
          <button className="btn btn-primary" onClick={() => { setEditingListing(null); setShowForm(true); }}>
            <Icon name="plus" size={16} />
            New Listing
          </button>
        </div>

        {/* Filters */}
        <div className="card p-4 mb-6">
          <div className="flex flex-wrap gap-4">
            <div style={{ position: 'relative', flex: 1 }}>
              <Icon name="search" size={16} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--slate-400)' }} />
              <input
                className="input input-with-icon"
                placeholder="Search by address, city, or title..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <div className="tabs">
              {['all', 'active', 'pending', 'sold'].map(status => (
                <button
                  key={status}
                  className={`tab ${statusFilter === status ? 'active' : ''}`}
                  onClick={() => setStatusFilter(status)}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </button>
              ))}
            </div>

            <select className="input select" style={{ width: '10rem' }} value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
              <option value="all">All Types</option>
              <option value="single_family">Single Family</option>
              <option value="condo">Condo</option>
              <option value="townhouse">Townhouse</option>
              <option value="multi_family">Multi Family</option>
              <option value="land">Land</option>
              <option value="commercial">Commercial</option>
            </select>

            <div style={{ display: 'flex', border: '1px solid var(--slate-200)', borderRadius: '0.5rem' }}>
              <button
                className={`btn btn-icon ${viewMode === 'grid' ? 'btn-outline' : 'btn-ghost'}`}
                style={{ borderRadius: '0.5rem 0 0 0.5rem', border: 'none' }}
                onClick={() => setViewMode('grid')}
              >
                <Icon name="grid" size={16} />
              </button>
              <button
                className={`btn btn-icon ${viewMode === 'list' ? 'btn-outline' : 'btn-ghost'}`}
                style={{ borderRadius: '0 0.5rem 0.5rem 0', border: 'none' }}
                onClick={() => setViewMode('list')}
              >
                <Icon name="list" size={16} />
              </button>
            </div>
          </div>
        </div>

        {/* Listings */}
        {loading ? (
          <div className={`grid ${viewMode === 'grid' ? 'grid-3' : ''} gap-4`}>
            {[1, 2, 3, 4, 5, 6].map(i => <div key={i} className="skeleton" style={{ height: '20rem' }} />)}
          </div>
        ) : filteredListings.length > 0 ? (
          <div className={`grid ${viewMode === 'grid' ? 'grid-3' : ''} gap-4`}>
            {filteredListings.map(listing => (
              <ListingCard key={listing.id} listing={listing} onEdit={handleEdit} />
            ))}
          </div>
        ) : (
          <div className="card" style={{ textAlign: 'center', padding: '4rem' }}>
            <Icon name="home" size={48} style={{ color: 'var(--slate-300)', margin: '0 auto 1rem' }} />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No listings found</h3>
            <p className="text-slate-500 mb-4">
              {search ? 'Try adjusting your search or filters' : 'Create your first property listing'}
            </p>
            <button className="btn btn-primary" onClick={() => { setEditingListing(null); setShowForm(true); }}>
              <Icon name="plus" size={16} />
              New Listing
            </button>
          </div>
        )}
      </div>

      <ListingForm
        open={showForm}
        onClose={() => { setShowForm(false); setEditingListing(null); }}
        listing={editingListing}
        agents={agents.filter(a => a.status === 'active')}
        onSave={handleSave}
      />
    </div>
  );
}